/**
 * Every change sent to the server says who is sending it.
 *
 * The audit log (سجل الحركات) puts every add, edit and delete down to the
 * person who made it, and the server reads who that is from the sign-in
 * token. Most of the dashboard's calls were written without one - the old
 * routes never asked - so this adds it to every write going to the API,
 * from every screen, old and new, without touching any of them.
 *
 * Writes only (POST, PUT, PATCH, DELETE): reads are not changes, and a GET
 * without the header needs no extra CORS round trip. A call that already
 * carries an Authorization header keeps its own.
 */

const API = /\/anas_backend\/api\//;
const WRITES = new Set(["POST", "PUT", "PATCH", "DELETE"]);

/** The signed-in token, or null - an old session stored the word "undefined". */
export const currentToken = (storage = window.sessionStorage) => {
  let token = null;
  try {
    token = storage.getItem("token");
  } catch (e) {
    return null;
  }
  return token && token !== "undefined" && token !== "null" ? token : null;
};

const hasAuth = (headers) => {
  if (!headers) return false;
  if (typeof headers.has === "function") return headers.has("Authorization");
  if (Array.isArray(headers))
    return headers.some(([k]) => String(k).toLowerCase() === "authorization");
  return Object.keys(headers).some((k) => k.toLowerCase() === "authorization");
};

const withAuth = (headers, value) => {
  if (headers && typeof headers.set === "function") {
    const copy = new headers.constructor(headers);
    copy.set("Authorization", value);
    return copy;
  }
  if (Array.isArray(headers)) return [...headers, ["Authorization", value]];
  return { ...(headers || {}), Authorization: value };
};

/** Whether a call is a write to the API that should say who sent it. */
export const needsAuth = (url, method) =>
  WRITES.has(String(method || "GET").toUpperCase()) && API.test(String(url || ""));

export default function installAuthToken(win, http) {
  if (win && typeof win.fetch === "function" && !win.fetch.authTokenInstalled) {
    const inner = win.fetch;
    const signedFetch = (input, init = {}) => {
      const url = typeof input === "string" ? input : input?.url;
      const method = init.method || (typeof input === "string" ? "GET" : input?.method);
      const token = currentToken(win.sessionStorage);

      if (!token || !needsAuth(url, method)) return inner(input, init);

      const given = init.headers || (typeof input === "string" ? undefined : input?.headers);
      if (hasAuth(given)) return inner(input, init);

      return inner(input, { ...init, headers: withAuth(given, `Bearer ${token}`) });
    };

    // keep what the fetch underneath says about itself (utils/noCache)
    Object.assign(signedFetch, inner);
    signedFetch.authTokenInstalled = true;
    // eslint-disable-next-line no-param-reassign
    win.fetch = signedFetch;
  }

  if (http && http.interceptors && !http.authTokenInstalled) {
    http.interceptors.request.use((config) => {
      const url = `${config.baseURL || ""}${config.url || ""}`;
      const token = currentToken(win?.sessionStorage);

      if (!token || !needsAuth(url, config.method) || hasAuth(config.headers)) return config;

      return {
        ...config,
        headers: { ...(config.headers || {}), Authorization: `Bearer ${token}` },
      };
    });

    // eslint-disable-next-line no-param-reassign
    http.authTokenInstalled = true;
  }
}
